import { parseISO, isValid, format } from 'date-fns';

/**
 * Parses a date value into a valid Date object or null.
 * @param val - Date string (ISO 8601), epoch timestamp in milliseconds, or Date object
 * @returns Valid Date object or null if the input is invalid
 */
function parseDate(val: unknown): Date | null {
  let date: Date | null = null;

  if (typeof val === 'string') {
    date = parseISO(val);
  } else if (typeof val === 'number') {
    date = new Date(val);
  } else if (val instanceof Date) {
    date = val;
  }

  return date && isValid(date) ? date : null;
}

/**
 * Safely formats a date value to a string using the specified format.
 * @param val - Date string (ISO 8601), epoch timestamp in milliseconds, or Date object
 * @param fmt - Date format string (default: 'MM-dd-yyyy')
 * @param invalidText - Text to return if the date is invalid (default: '')
 * @returns Formatted date string or invalidText if the date is invalid
 */
export function safeFormatDate(val: unknown, fmt = 'MM-dd-yyyy', invalidText = ''): string {
  const date = parseDate(val);
  return date ? format(date, fmt) : invalidText;
}

/**
 * Safely converts a date value to an ISO 8601 string.
 * @param val - Date string (ISO 8601), epoch timestamp in milliseconds, or Date object
 * @returns ISO 8601 string (e.g., '2024-01-15T10:30:00.000Z') or undefined if the date is invalid
 */
export function safeDateToISOString(val: unknown): string | undefined {
  const date = parseDate(val);
  return date ? date.toISOString() : undefined;
}
